import { migrateResultsData, shouldMigrate, validateMigratedData } from './categoryMigration';
import { logMigrationEvent, logMigrationError } from './migrationLogger';

// Current app version - increment when making breaking changes
// 2.1.0: quiz content rewrite (13 -> 20 personality questions; QUESTIONS.length 17 -> 24).
// 3.0.0: McKayla Sept 2026 quiz rewrite (20 -> 15 personality questions; QUESTIONS.length 24 -> 19).
export const APP_VERSION = '3.0.0';

// localStorage key for storing app version
const VERSION_KEY = 'mpay_app_version';

/**
 * Gets the stored app version from localStorage
 */
export function getStoredVersion(): string | null {
  try {
    return localStorage.getItem(VERSION_KEY);
  } catch (error) {
    console.error('Error reading stored version:', error);
    return null;
  }
}

/**
 * Updates the stored app version in localStorage
 */
export function updateStoredVersion(): void {
  try {
    localStorage.setItem(VERSION_KEY, APP_VERSION);
  } catch (error) {
    console.error('Error updating stored version:', error);
  }
}

/**
 * Checks if the stored version is compatible with current version
 */
export function isVersionCompatible(storedVersion: string | null): boolean {
  // If no version is stored, assume it's from before versioning (incompatible)
  if (!storedVersion) {
    return false;
  }

  // For now, we'll use exact matching
  // In the future, we could implement semver-style compatibility checking
  return storedVersion === APP_VERSION;
}

/**
 * Handles version mismatch by migrating data and updating version
 */
export function handleVersionMismatch(resultsData: any): any {
  try {
    logMigrationEvent('version_mismatch_detected', {
      storedVersion: getStoredVersion(),
      currentVersion: APP_VERSION,
      dataType: typeof resultsData
    });

    // Check if migration is actually needed
    if (!shouldMigrate(resultsData)) {
      // No migration needed, just update version
      updateStoredVersion();
      logMigrationEvent('version_updated_no_migration', { version: APP_VERSION });
      return resultsData;
    }

    // Perform migration
    const migratedData = migrateResultsData(resultsData);

    // Validate migration success
    if (!validateMigratedData(migratedData)) {
      throw new Error('Migration validation failed');
    }

    // Update version after successful migration
    updateStoredVersion();

    logMigrationEvent('migration_completed', {
      fromVersion: getStoredVersion(),
      toVersion: APP_VERSION,
      success: true
    });

    return migratedData;

  } catch (error) {
    logMigrationError(error as Error, {
      storedVersion: getStoredVersion(),
      currentVersion: APP_VERSION,
      originalData: resultsData
    });

    // Return original data if migration fails
    // This prevents crashes but may cause display issues
    console.warn('Migration failed, returning original data');
    return resultsData;
  }
}

/**
 * Forces a version reset (clears stored version)
 * Useful for testing or forcing re-migration
 */
export function resetVersion(): void {
  try {
    localStorage.removeItem(VERSION_KEY);
    logMigrationEvent('version_reset', { resetAt: new Date().toISOString() });
  } catch (error) {
    console.error('Error resetting version:', error);
  }
}

/**
 * Gets version information for debugging
 */
export function getVersionInfo(): {
  current: string;
  stored: string | null;
  compatible: boolean;
  lastMigration?: string;
} {
  const storedVersion = getStoredVersion();
  
  return {
    current: APP_VERSION,
    stored: storedVersion,
    compatible: isVersionCompatible(storedVersion),
    lastMigration: localStorage.getItem('mpay_last_migration') || undefined
  };
}

/**
 * Checks if this is a fresh install (no previous data)
 */
export function isFreshInstall(): boolean {
  const hasStoredData = localStorage.getItem('mpay_resultsData');
  const hasStoredVersion = getStoredVersion();
  
  return !hasStoredData && !hasStoredVersion;
}

/**
 * Initializes version tracking for new installations
 */
export function initializeVersionTracking(): void {
  if (isFreshInstall()) {
    updateStoredVersion();
    logMigrationEvent('fresh_install', { version: APP_VERSION });
  }
}

/**
 * Main function to handle data loading with version compatibility
 */
export function loadDataWithVersionCheck(
  data: any,
  source: 'localStorage' | 'database' | 'api' = 'localStorage'
): any {
  try {
    // For fresh installs, just set up version tracking
    if (isFreshInstall() && source === 'localStorage') {
      initializeVersionTracking();
      return data;
    }

    // Check version compatibility
    const storedVersion = getStoredVersion();
    const compatible = isVersionCompatible(storedVersion);
    const needsMigration = shouldMigrate(data);

    // If compatible and no migration needed, return data as-is
    if (compatible && !needsMigration) {
      return data;
    }

    // If version mismatch but no migration needed, just update version
    if (!compatible && !needsMigration) {
      updateStoredVersion();
      logMigrationEvent('version_updated_existing_user', { 
        previousVersion: storedVersion,
        newVersion: APP_VERSION 
      });
      return data;
    }

    // Handle cases that actually need migration
    const processedData = handleVersionMismatch(data);

    // Update localStorage if this was from localStorage and migration occurred
    if (source === 'localStorage' && processedData !== data) {
      try {
        localStorage.setItem('mpay_resultsData', JSON.stringify(processedData));
        localStorage.setItem('mpay_last_migration', new Date().toISOString());
      } catch (error) {
        console.error('Error updating localStorage after migration:', error);
      }
    }

    return processedData;

  } catch (error) {
    logMigrationError(error as Error, {
      source,
      dataType: typeof data
    });
    
    // Return original data if version check fails
    return data;
  }
}